import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AnimatedLoader,
    CustomerDetails,
    Error,
    DueDays,
    InvoiceDetails,
    InvoiceStatus,
    Attachments,
    InvoiceDownload
} from '../../components';
import { Clamp, Text } from '../../helpers';
import { IInvoice } from '../../interfaces';
import useStripePaymentConfirmation from './useStripePaymentConfirmation';

type Props = {
    invoice: IInvoice;
};

const InvoierCheckout = (props: Props) => {
    const location = useLocation();
    const { t } = useTranslation();
    const { confirming, invoice } = useStripePaymentConfirmation(props.invoice);

    if (location.state?.failed === 'redirected' && !invoice.is_signed) {
        return (
            <Clamp>
                <InvoiceStatus
                    status="failed"
                    withAttachments={!!invoice.attachments[0]?.storage_name}
                />
                <CustomerDetails status="failed" />
                <InvoiceDownload invoice={invoice} />
                <DueDays invoiceId={invoice.uuid} />
                <Attachments invoice={invoice} clickable={false} />
                <InvoiceDetails />
            </Clamp>
        );
    }

    if (confirming) {
        return (
            <Clamp>
                <AnimatedLoader />
                <Text
                    i={t('confirmingPayment')}
                    fontWeight="normal"
                    style={{ fontSize: '18px', textAlign: 'center', marginTop: '40%' }}
                />
            </Clamp>
        );
    }

    if (invoice.is_signed === null) {
        return <Error message={t('invoiceNotFound')} />;
    }

    if (invoice.status === 'cancelled') {
        return (
            <Clamp amount={10}>
                <Error
                    invoice={invoice}
                    message={t('invoiceCancelled')}
                    reason="cancelled"
                />
            </Clamp>
        );
    }

    if (invoice.is_signed) {
        return (
            <Clamp>
                <InvoiceStatus
                    status="success"
                    withAttachments={!!invoice.attachments[0]?.storage_name}
                />
                <CustomerDetails />
                <InvoiceDownload invoice={invoice} />
                <Attachments invoice={invoice} clickable={false} />
            </Clamp>
        );
    }

    return (
        <Clamp>
            <CustomerDetails />
            <InvoiceDownload invoice={invoice} />
            <DueDays invoiceId={invoice.uuid} />
            <Attachments invoice={invoice} />
            <InvoiceDetails />
        </Clamp>
    );
};

export default InvoierCheckout;
